import anchorme from "anchorme";
import moment from "moment";

import { Icon, Message, Statistic } from "semantic-ui-react";

import React from "react";
import { useTranslation, withTranslation } from "react-i18next";

import { durationToSec, toDate } from "astrid-web/src/helpers/fnc";

import ProductionStatusLadder from "../../../features/productions/components/ProductionStatusLadder/ProductionStatusLadder";
import getStudioHours from "../../../features/productions/utils/getStudioHours";

import ProgressEdit from "../ProgressEdit";
import ProgressRecording from "../ProgressRecording";

const ProductionStatus = ({
	isProducer,
	isPublisher,
	isReader,
	user,
	producer,
	publisher,
	production,
	handleChange,
}) => {
	const isManager = (isProducer && isProducer?.includes("producerAdmin")) || production?.manager?.includes(user.uid);
	const isSubcontractorProduction = production?.productionType === "subcontract";
	const clickClass = isManager ? " pointer" : "";
	const { t } = useTranslation();

	const { remainingStudioHours } = getStudioHours(production);

	const setStatus = (status) => {
		if (window.confirm(t("changeProdStatus"))) {
			handleChange(null, { name: "status", value: status });
		}
	};

	const estimatedDuration = production.estimatedDuration?.slice(0, -3);

	return (
		<>
			{production.status === "draft" && (
				<Message icon floating warning>
					<Icon name="warning sign" color="orange" />
					<Message.Content>
						<b>{t("draft")} </b>
						{t("notShownYet")}
					</Message.Content>
				</Message>
			)}

			{production.status === "done" && (
				<Message icon floating style={{ background: "var(--color-bg-card)" }}>
					<Icon name="paper plane outline" color="green" />
					<Message.Content>
						<div className="flex-stack" style={{ alignItems: "center" }}>
							<div style={{ minWidth: "50%" }}>
								<Message.Header>{t("prodDelivered")}</Message.Header>

								{production.deliveryDateActual &&
									// producers can always see exact date. publishers can see starting from april 9th 2020.
									(isProducer || toDate(production.deliveryDateActual) > new Date("2020-04-09")) && (
										<div>
											{t("wasFinalized")}{" "}
											{moment(toDate(production.deliveryDateActual)).format("YYYY-MM-DD HH:mm")}
										</div>
									)}
								{production.deliveryDuration && t("duration") + ": " + production.deliveryDuration}

								{production.deliveryMessage &&
									(!production.deliveryDuration ||
										durationToSec(production.deliveryDuration) !==
											durationToSec(production.deliveryMessage)) && (
										<div
											className="line-broken"
											dangerouslySetInnerHTML={{
												__html: anchorme(production.deliveryMessage, {
													truncate: [26, 15],
													attributes: [
														function (urlObj) {
															if (urlObj.protocol !== "mailto:")
																return { name: "target", value: "blank" };
														},
													],
												}),
											}}
										/>
									)}
							</div>
						</div>
					</Message.Content>
				</Message>
			)}

			<div style={{ margin: "20px 0" }}>
				<ProductionStatusLadder production={production} />
			</div>

			<div style={{ textAlign: "center" }} className="production-statistics clear">
				{isPublisher ? (
					<div style={{ marginBottom: "1em" }}>
						{production.stats && ["production", "done"].includes(production.status) && (
							<>
								<ProgressRecording label={t("recPages")} production={production} />

								{production.stats.cubase && (
									<Statistic horizontal size="mini" color="orange">
										<Statistic.Value>
											<Icon name="play" />
											&nbsp;
											{estimatedDuration}
										</Statistic.Value>
										<Statistic.Label>{t("calcDuration")}</Statistic.Label>
									</Statistic>
								)}
							</>
						)}
					</div>
				) : (
					<>
						{(production.stats || production.progress) && (
							<>
								{!(isReader && production.status === "done") && production.stats && (
									<ProgressRecording production={production} />
								)}
								{!isReader &&
									(!production.productionType || production.productionType === "subcontract") && (
										<ProgressEdit production={production} />
									)}
								{production.stats?.tempo && production.status !== "done" && (
									<>
										<Statistic horizontal size="mini" color="orange">
											<Statistic.Value>
												<Icon name="play" />
												&nbsp;
												{estimatedDuration}
											</Statistic.Value>
											<Statistic.Label>{t("calcDuration")}</Statistic.Label>
										</Statistic>
										<Statistic horizontal size="mini" color="orange">
											<Statistic.Value>
												<Icon name="clock" />
												&nbsp;
												{remainingStudioHours}
											</Statistic.Value>
											<Statistic.Label>{t("studioTimeLeft")}</Statistic.Label>
										</Statistic>
									</>
								)}
								{!isReader && production.stats && (
									<>
										{production.stats.tempo && (
											<Statistic horizontal size="mini" color="teal">
												<Statistic.Value>
													<Icon name="file text" />
													&nbsp;
													{Math.round(production.stats.tempo.average * 100) / 100}
												</Statistic.Value>
												<Statistic.Label>{t("recTempo")}</Statistic.Label>
											</Statistic>
										)}
										{production.stats.ratio && (
											<Statistic horizontal size="mini" color="teal">
												<Statistic.Value>
													<Icon name="microphone" />
													&nbsp;
													{Math.round(production.stats.ratio.average)} %
												</Statistic.Value>
												<Statistic.Label>{t("recRatio")}</Statistic.Label>
											</Statistic>
										)}
										{production.stats.loss && (
											<Statistic horizontal size="mini" color="teal">
												<Statistic.Value>
													<Icon name="trash alternate" />
													&nbsp;
													{Math.round(production.stats.loss.average)} %
												</Statistic.Value>
												<Statistic.Label>{t("recLoss")}</Statistic.Label>
											</Statistic>
										)}
									</>
								)}
							</>
						)}
						{/* {isManager && <ProductionDelegation production={production} producer={producer} />} */}
						{isSubcontractorProduction && !isPublisher && (
							<div style={{ float: "right" }}>
								{production.subcontractorStatus === "declined" ? (
									<>
										<Icon name="exclamation" color="red" />
										<span style={{ opacity: "80%" }}>{t("subcontractorProductionDeclined")}</span>
									</>
								) : (
									<>
										<Icon name="wrench" color="green" />
										<span style={{ opacity: "80%" }}>
											{t("subcontractorProduction")}
											{production?.proofer?.includes("NONE")
												? ", " + t("subcontractorProductionNoProofer")
												: ""}
										</span>
									</>
								)}
							</div>
						)}
					</>
				)}
			</div>
		</>
	);
};

export default withTranslation()(ProductionStatus);
